import {hasFeatureFlag} from '../../forecast-app/shared/util/FeatureUtil';
import {hasPermission, hasSomePermission} from '../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../Permissions';
import {withPeriodFilterConditions} from './periodFilter';

/** This report's own flag, and nobody else's.
 *
 * Deliberately NOT the report builder's flags: this is a finance compliance report, and tying it to
 * the AI builder's rollout would make an auditability feature wait on an unrelated one. It renders
 * on the builder's surface as an implementation detail, which is not a reason to inherit that
 * surface's release gate.
 *
 * Deliberately NOT `timesheet_approval_audit_log` either, though both read the same table. That one
 * gates the per-registration history drawer in timesheets; this gates a report in Reports, for a
 * different reader, and the two roll out on their own schedules. Sharing a flag would mean neither
 * could be turned on for a company without the other.
 *
 * The same key gates the server read (`TimeRegApprovalLogLogic.search`), so the card cannot appear
 * over a report that is guaranteed to come back empty. */
export const AUDIT_LOG_FEATURE_FLAG = 'timesheet_approval_audit_log_report';

/** Display name of the audit-log report. Matches report-service's `ReportType.TIME_REG_AUDIT_LOG`
 * display string exactly — that string is what a saved report stores in its `type` column. */
export const AUDIT_LOG_REPORT_TYPE = 'Time Approval Audit Log';

/** Taxonomy catalog name for the audit-log grain, and its `ReportGrain` token. Resolving a
 * definition to it is `reportGrain.ts`'s job -- nothing here re-derives what that registry owns. */
export const AUDIT_LOG_TAXONOMY_TYPE = 'TIME_REG_AUDIT_LOG';

/**
 * Whether this viewer may open the Time Approval Audit Log report.
 *
 * Three gates. The flag is the audit log's own, so this report ships on its own schedule rather than
 * the report builder's.
 *
 * `INSIGHTS_READ` is the report-access gate every report surface applies. The last is
 * admin-or-controller, matching what service-company enforces on the company-wide audit read: the
 * default profiles are cumulative and Controller is exactly Manager plus
 * VIEW_FINANCIAL_INFORMATION, so that permission separates Controller-and-above from
 * Manager-and-below. This is the client half of that check — the authoritative one is server-side,
 * because a hidden card is not an access control.
 */
export function canOpenAuditLogReport(): boolean {
	return (
		hasFeatureFlag(AUDIT_LOG_FEATURE_FLAG) &&
		hasPermission(PERMISSION_TYPE.INSIGHTS_READ) &&
		hasSomePermission([PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS, PERMISSION_TYPE.VIEW_FINANCIAL_INFORMATION])
	);
}

/**
 * The audit log's default columns, in order, mirroring `defaultColumns` in report-service's
 * `taxonomy/time_reg_audit_log.json`. Restated here because a fresh report needs its columns before
 * any taxonomy has resolved — the definition, not the catalog, is what the table renders from.
 */
export const AUDIT_LOG_DEFAULT_COLUMNS: string[] = [
	'timeRegAuditEvent.timestamp',
	'timeRegAuditEvent.person.fullName',
	'timeRegAuditEvent.timeType',
	'timeRegAuditEvent.eventType',
	'timeRegAuditEvent.fromStatus',
	'timeRegAuditEvent.toStatus',
	'timeRegAuditEvent.actor.fullName',
	'timeRegAuditEvent.project.info',
	'timeRegAuditEvent.workDate',
	'timeRegAuditEvent.hours',
	'timeRegAuditEvent.reason',
];

export interface AuditLogFieldNode {
	name: string;
	children?: AuditLogFieldNode[];
}

/**
 * Turn dotted column paths into the nested field tree a report definition carries.
 *
 * The tree is what the fetch selects on, so a path that does not appear here resolves to nothing and
 * renders an empty column. Shared prefixes merge into one node — `person.firstName` and
 * `person.lastName` are two children of one `person`, not two `person` nodes, which is what the
 * server's own field filtering expects.
 */
export function fieldTreeFromPaths(paths: string[]): AuditLogFieldNode[] {
	const roots: AuditLogFieldNode[] = [];

	for (const path of paths) {
		let siblings = roots;
		for (const segment of path.split('.')) {
			let node = siblings.find(candidate => candidate.name === segment);
			if (!node) {
				node = {name: segment};
				siblings.push(node);
			}
			if (!node.children) {
				node.children = [];
			}
			siblings = node.children;
		}
	}

	// A leaf keeps an empty `children` array rather than dropping the key: that is the shape the
	// existing definitions use, and the field converter reads it either way.
	return roots;
}

/**
 * A fresh, unsaved audit-log report for the given period.
 *
 * The period is stated as literal dates AND as the task grain's overlap-pair conditions, because
 * that pair is the only way the builder carries a period to the fetch — its request body has no date
 * range. For a point-in-time audit event those two bounds are exactly "the event falls in the
 * period", which is what the audit read interprets them as.
 */
/** A fresh audit-log definition. Typed structurally rather than as `ReportDefinitionV1Tasks`: that
 * type is generated from the vendored C1 schema, whose `reportType` enum does not yet name this
 * grain, and the schema's canonical source lives outside this repo (see the component's CLAUDE.md).
 * The shape below is the subset the builder reads. */
export interface AuditLogReportDefinition {
	schemaVersion: string;
	/** Also on the definition, not only on the payload: `buildSaveReportAsInput` reads
	 * `definition.reportType` for the mutation's `type`, and a definition without it saves as a task
	 * report — which then reopens on the wrong grain, with another report's columns. */
	reportType: string;
	name: string;
	startDate: string;
	endDate: string;
	fields: AuditLogFieldNode[];
	filter: {
		type: 'SIMPLE_AND';
		filterConditions: {field: string; operator: string; values: string[]; context: string}[];
	};
	layout: {field: string}[];
	sort: {field: string; direction: 'ASC' | 'DESC'};
}

export interface AuditLogReportPayload {
	reportType: string;
	suggestedName: string;
	/** Authored by the Create-a-New-Report card, not by the agent. The builder shows a
	 * "Generated from the prompt" banner for an AI-authored report, which would be simply untrue
	 * here -- there was no prompt. */
	source: 'STANDARD';
	definition: AuditLogReportDefinition;
}

export function newAuditLogReportPayload(
	startDate: string,
	endDate: string,
	name = 'Time Approval Audit Log'
): AuditLogReportPayload {
	return {
		reportType: AUDIT_LOG_REPORT_TYPE,
		suggestedName: name,
		source: 'STANDARD',
		definition: {
			schemaVersion: '1.0',
			reportType: AUDIT_LOG_REPORT_TYPE,
			name,
			startDate,
			endDate,
			fields: fieldTreeFromPaths(AUDIT_LOG_DEFAULT_COLUMNS),
			// Built by the same function the date control uses, rather than written out here. A
			// literal pair drifted the moment this grain got its own period field: the payload kept
			// writing the task grain's START_DATE/END_DATE while every later period change wrote
			// AUDIT_EVENT_DATE, so a fresh report was filtered on one field and re-filtered on
			// another. report-service still reads the old pair as an event-date window, which is what
			// keeps already-saved reports working, but nothing new should be written that way.
			filter: {
				type: 'SIMPLE_AND',
				filterConditions: withPeriodFilterConditions(
					[],
					startDate,
					endDate,
					'all_dates',
					AUDIT_LOG_TAXONOMY_TYPE
				) as AuditLogReportDefinition['filter']['filterConditions'],
			},
			layout: AUDIT_LOG_DEFAULT_COLUMNS.map(field => ({field})),
			sort: {field: 'timeRegAuditEvent.timestamp', direction: 'DESC'},
		},
	};
}
